import { ChangeDetectorRef, Component, inject, Injector, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StorageService } from '../../../services/storage/storage.service';
import { NgbActiveModal, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { OfflineModeService } from '../../../services/offline-mode.service';
import {
	PROJECT_PROVIDER, AUTH_PROVIDER, KEYCLOAK_ADMIN_SERVICE, PROJECT_ROLES_CONFIG,
	ProjectMemberRoleConfig, STRUCTURE_ASSIGNMENT_CONFIGS, StructureAssignmentConfig,
	PROJECT_NEW_SECTIONS, ProjectNewSection,
	PROJECT_POST_CREATE_HANDLERS, ProjectPostCreateHandler,
	PROJECT_ENTITY_LABEL
} from '../../../core';
import { AttachmentsListComponent } from '../attachments/attachments-list.component';
import { ProjectStructureAssignmentComponent } from './project-structure-assignment.component';

export interface UserWithRoles {
	id: string;
	username: string;
	email?: string;
	firstName?: string;
	lastName?: string;
	enabled: boolean;
	roles?: any[];
	clientRoles?: any[];
	selected?: boolean;
}

@Component({
    selector: 'bp-project-new',
    standalone: true,
    imports: [CommonModule, FormsModule, NgbAccordionModule, AttachmentsListComponent, ProjectStructureAssignmentComponent, NgComponentOutlet],
    templateUrl: './project-new.component.html',
    styleUrls: ['./project-new.component.css', '../../accordion.css'],
    encapsulation: ViewEncapsulation.None
})
export class ProjectNewComponent implements OnInit {
	activeModal = inject(NgbActiveModal);
	storage = inject(StorageService);
	keycloakAdmin = inject(KEYCLOAK_ADMIN_SERVICE);
	auth: any = inject(AUTH_PROVIDER);
	projectService: any = inject(PROJECT_PROVIDER);
	oms = inject(OfflineModeService);
	rolesConfig = inject(PROJECT_ROLES_CONFIG);
	assignmentConfigs = inject(STRUCTURE_ASSIGNMENT_CONFIGS);
	newSections = inject(PROJECT_NEW_SECTIONS);
	private postCreateHandlers = inject(PROJECT_POST_CREATE_HANDLERS);
	entityLabel = inject(PROJECT_ENTITY_LABEL);
	private parentInjector = inject(Injector);
	private cdr = inject(ChangeDetectorRef);

	/** Daten aus dynamischen Sektionen, gesammelt per setSectionData() */
	sectionData = new Map<string, any>();

	/** Custom Injector der den ProjectNewComponent für Kinder verfügbar macht */
	sectionInjector = Injector.create({
		providers: [{ provide: ProjectNewComponent, useValue: this }],
		parent: this.parentInjector,
	});

	// Wird von außen gesetzt für Bearbeiten-Modus
	private _editProjectId?: string;

	get editProjectId(): string | undefined {
		return this._editProjectId;
	}

	set editProjectId(value: string | undefined) {
		// Nur reagieren wenn sich der Wert wirklich ändert
		if (this._editProjectId === value) return;
		this._editProjectId = value;
		// Wenn editProjectId gesetzt wird, Projekt neu laden
		if (value) {
			this.initProject();
		}
	}

	project!: any;
	originalName = '';
	users: UserWithRoles[] = [];
	loadingUsers = false;
	usersError = '';
	isSaving = false;
	private initializingProject = false;

	get isEditMode(): boolean {
		return !!this.editProjectId;
	}

	get dialogTitle(): string {
		return this.isEditMode ? `${this.entityLabel.name} bearbeiten` : this.entityLabel.newLabel;
	}

	// Dynamische Rollen-basierte User-Listen
	membersByRole = new Map<string, UserWithRoles[]>();

	/**
	 * Liefert verfügbare Nutzer für eine Rolle, ohne bereits in anderen Rollen ausgewählte.
	 */
	getAvailableMembers(role: ProjectMemberRoleConfig): UserWithRoles[] {
		const allForRole = this.membersByRole.get(role.field) ?? [];
		// IDs der in ANDEREN Rollen ausgewählten Nutzer sammeln
		const selectedInOtherRoles = new Set<string>();
		for (const otherRole of this.rolesConfig.memberRoles) {
			if (otherRole.field === role.field) continue;
			const otherUsers = this.membersByRole.get(otherRole.field) ?? [];
			otherUsers.filter(u => u.selected).forEach(u => selectedInOtherRoles.add(u.id));
		}
		return allForRole.filter(u => !selectedInOtherRoles.has(u.id));
	}

	/**
	 * Prüft ob eine Rolle sichtbar sein soll (basierend auf dependsOnPermission).
	 */
	isRoleVisible(role: ProjectMemberRoleConfig): boolean {
		if (!role.dependsOnPermission) return true;
		// Prüfe ob die erste Rolle (ohne dependsOnPermission) ausgewählte Mitglieder hat
		const firstRole = this.rolesConfig.memberRoles.find(r => !r.dependsOnPermission);
		if (firstRole) {
			const hasSelected = (this.membersByRole.get(firstRole.field) ?? []).some(u => u.selected);
			if (!hasSelected) return false;
		}
		return !!this.project?.permissions?.[role.dependsOnPermission];
	}

	get isNameTaken(): boolean {
		if (this.isSaving) return false;
		if (!this.project?.name) return false;
		return this.isNameTakenOrNoProject;
	}

	get isNameTakenOrNoProject(): boolean {
		if (this.isSaving) return false;
		if (!this.project?.name) return true;
		const name = this.project.name.trim().toLowerCase();
		// Im Bearbeiten-Modus: Erlaube den aktuellen Namen
		if (this.isEditMode && name === this.originalName.toLowerCase()) return false;
		return this.projectService.projectList().some((p: any) => p.name?.toLowerCase() === name);
	}

	get isUserManager(): boolean {
		return this.auth.hasRealmRole('app-user-manager');
	}

	/**
	 * Prüft ob der aktuelle Benutzer ein Projekt-Manager ist.
	 */
	get isManager(): boolean {
		return this.auth.canIbe('manager');
	}

	/**
	 * Prüft ob der aktuelle Benutzer der Owner des Projekts ist.
	 */
	get isOwner(): boolean {
		return this.project?.owner?.id === this.auth.currentUserId;
	}

	/**
	 * Prüft ob der aktuelle Benutzer Berechtigungen ändern darf.
	 * Nur Manager und Owner dürfen Berechtigungen ändern.
	 */
	get canEditPermissions(): boolean {
		return this.isManager || this.isOwner;
	}

	/**
	 * Prüft ob der Owner (= aktueller Benutzer) Punkte anlegen darf.
	 */
	get canOwnerAdd(): boolean {
		return this.auth.canI('add', 'points');
	}

	/**
	 * Prüft ob der Owner (= aktueller Benutzer) Punkte ändern darf.
	 */
	get canOwnerWrite(): boolean {
		return this.auth.canI('write', 'points');
	}

	get canManageParticipants(): boolean {
		// Teilnehmer nur bearbeiten wenn:
		// 1. Projekt-Manager (bpweb client role) UND
		// 2. User-Manager (Keycloak realm role für Admin-Zugriff) UND
		// 3. Internetverbindung vorhanden
		return this.isManager && this.isUserManager && this.oms.networkOnline();
	}

	/** Setzt den Bearbeiten-Modus mit einem existierenden Projekt */
	setEditMode(project: { _id: string }): void {
		this.editProjectId = project._id;
	}

	ngOnInit(): void {
		// Verzögert initialisieren, damit editProjectId von außen gesetzt werden kann
		// (NgbModal setzt componentInstance.editProjectId NACH der Komponenten-Erstellung)
		Promise.resolve().then(() => {
			if (!this.project) {
				this.initProject();
			}
		});
	}

	private async initProject(): Promise<void> {
		// Verhindere doppelte Initialisierung
		if (this.initializingProject) return;
		this.initializingProject = true;

		console.log('[ProjectNew] initProject called, isEditMode:', this.isEditMode, 'editProjectId:', this.editProjectId);
		if (this.isEditMode) {
			// Bearbeiten-Modus: Lade existierendes Projekt
			const projectDoc = await this.projectService.getOne(this.editProjectId!) as any;
			console.log('[ProjectNew] Loaded projectDoc:', projectDoc);
			console.log('[ProjectNew] projectDoc.permissions:', projectDoc?.permissions);
			if (projectDoc) {
				// Owner kann entweder als Objekt {id, username} oder als String (userId) gespeichert sein
				let owner = projectDoc.owner;
				if (!owner || typeof owner === 'string') {
					// Fallback: Owner aus userId oder createdBy erstellen
					const ownerId = projectDoc.owner || projectDoc.userId || projectDoc.createdBy;
					owner = ownerId ? { id: ownerId, username: projectDoc.ownerName || 'Unbekannt' } : undefined;
				}
				console.log('[ProjectNew] Resolved owner:', owner);

				const memberFields: any = {};
				for (const role of this.rolesConfig.memberRoles) {
					memberFields[role.field] = projectDoc[role.field] ?? [];
				}
				const assignmentFields: any = {};
				for (const ac of this.assignmentConfigs) {
					assignmentFields[ac.projectField] = projectDoc[ac.projectField] ?? [];
				}
				this.project = {
					name: projectDoc.name,
					description: projectDoc.description,
					owner: owner,
					...memberFields,
					...assignmentFields,
					permissions: projectDoc.permissions ?? this.defaultPermissions()
				};
				console.log('[ProjectNew] this.project.permissions after assignment:', this.project.permissions);
				this.originalName = projectDoc.name;
			}
		} else {
			// Neu-Modus: Leeres Projekt
			const memberFields: any = {};
			for (const role of this.rolesConfig.memberRoles) {
				memberFields[role.field] = [];
			}
			const assignmentFields: any = {};
			for (const ac of this.assignmentConfigs) {
				assignmentFields[ac.projectField] = [];
			}
			this.project = {
				name: "",
				description: "",
				owner: {
					id: this.auth.currentUserId,
					username: this.auth.currentUser
				},
				...memberFields,
				...assignmentFields,
				permissions: this.defaultPermissions()
			};
		}

		if (this.canManageParticipants) {
			this.loadUsers();
		}
	}

	private defaultPermissions(): any {
		// Für Nicht-Manager: Permissions basierend auf Auth-Rechten des Owners
		// Manager können alle Permissions setzen
		// Löschen ist für Owner-only-Projekte immer erlaubt
		if (this.isManager) {
			return {
				anlegen: true,
				loeschen: true,
				importieren: true,
				aendern: true,
				zuordnen: true,
				bereicheAnlegen: true,
				bereicheLoeschen: true
			};
		}
		return {
			anlegen: this.canOwnerAdd,
			loeschen: true,
			importieren: false,  // Import nur für Projekte mit Bearbeitern
			aendern: this.canOwnerWrite,
			zuordnen: true,      // Standard: Punkte zuordnen erlaubt
			bereicheAnlegen: true,   // Standard: Bereiche anlegen erlaubt
			bereicheLoeschen: false  // Standard: Bereiche löschen nicht erlaubt
		};
	}

	onCheckboxChange(field: string, event: Event): void {
		const checked = (event.target as HTMLInputElement).checked;
		if (!this.project.permissions) {
			this.project.permissions = {};
		}
		this.project.permissions[field] = checked;

		// Spezielle Logik für bestimmte Felder
		if (field === 'anlegen' || field === 'importieren') {
			this.onPermissionChange();
		} else if (field === 'aendern') {
			this.onAendernChange();
		}

		this.cdr.detectChanges();
	}

	togglePermission(field: string): void {
		if (!this.project.permissions) {
			this.project.permissions = {};
		}
		this.project.permissions[field] = !this.project.permissions[field];

		// Spezielle Logik für bestimmte Felder
		if (field === 'anlegen' || field === 'importieren') {
			this.onPermissionChange();
		} else if (field === 'aendern') {
			this.onAendernChange();
		}
	}

	onPermissionChange(): void {
		if (!this.project.permissions) return;
		// Keine automatische Verknüpfung mehr - jede Permission ist unabhängig
		this.cdr.detectChanges();
	}

	onAendernChange(): void {
		this.clearDependentMembers();
		this.cdr.detectChanges();
	}

	private clearDependentMembers(): void {
		for (const role of this.rolesConfig.memberRoles) {
			if (role.dependsOnPermission && !this.project.permissions?.[role.dependsOnPermission]) {
				const usersForRole = this.membersByRole.get(role.field) ?? [];
				usersForRole.forEach(u => u.selected = false);
				this.updateProjectMembers(role);
			}
		}
	}

	async loadUsers(): Promise<void> {
		this.loadingUsers = true;
		this.usersError = '';
		try {
			this.users = await this.keycloakAdmin.getUsers();
			await this.loadUserRoles();
			this.filterUsersByRole();
		} catch (err) {
			const msg = err instanceof Error ? err.message : String(err);
			this.usersError = 'Fehler beim Laden der Benutzer: ' + msg;
			console.error('Failed to load users:', msg);
		} finally {
			this.loadingUsers = false;
		}
	}

	private filterUsersByRole(): void {
		for (const role of this.rolesConfig.memberRoles) {
			const existingIds = new Set(this.project[role.field]?.map((k: any) => k.id) ?? []);
			const usersForRole = this.users
				.filter(user => this.userHasClientRole(user, role.authRole))
				.map(user => ({ ...user, selected: existingIds.has(user.id) }));
			this.membersByRole.set(role.field, usersForRole);
		}
	}

	private userHasClientRole(user: UserWithRoles, roleName: string): boolean {
		if (!user.clientRoles) return false;
		return user.clientRoles.some(role => role.name === roleName) ||
			user.clientRoles.some(role => role.children?.some((child: any) => child.name === roleName));
	}

	private async loadUserRoles(): Promise<void> {
		for (const user of this.users) {
			try {
				user.roles = await this.keycloakAdmin.getUserRolesExpanded(user.id);
			} catch (err) {
				console.warn(`Failed to load realm roles for user ${user.username}:`, err);
				user.roles = [];
			}
			try {
				user.clientRoles = await this.keycloakAdmin.getUserClientRolesExpanded(user.id, this.rolesConfig.keycloakClient);
			} catch (err) {
				console.warn(`Failed to load client roles for user ${user.username}:`, err);
				user.clientRoles = [];
			}
		}
	}

	toggleMember(role: ProjectMemberRoleConfig, user: UserWithRoles): void {
		user.selected = !user.selected;
		this.updateProjectMembers(role);
	}

	private updateProjectMembers(role: ProjectMemberRoleConfig): void {
		const usersForRole = this.membersByRole.get(role.field) ?? [];
		this.project[role.field] = usersForRole
			.filter(u => u.selected)
			.map(u => this.toProjectMember(u));
	}

	private toProjectMember(user: UserWithRoles): any {
		return {
			id: user.id,
			username: user.username,
			firstName: user.firstName,
			lastName: user.lastName
		};
	}

	/** Wird von dynamischen Sektions-Komponenten aufgerufen um Daten zu hinterlegen */
	setSectionData(sectionId: string, data: any): void {
		this.sectionData.set(sectionId, data);
	}

	async onSave() {
		// Netzwerk-Check: Im Online-Modus ohne Netzwerk kein Speichern
		if (!this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			alert('Keine Netzwerkverbindung - Projekt kann nicht gespeichert werden.');
			return;
		}
		this.isSaving = true;
		if (this.isEditMode) {
			// Bearbeiten: Existierendes Projekt aktualisieren
			const projectDoc = await this.projectService.getOne(this.editProjectId!) as any;
			if (projectDoc) {
				projectDoc.name = this.project.name;
				projectDoc.description = this.project.description;
				projectDoc.owner = this.project.owner;
				for (const role of this.rolesConfig.memberRoles) {
					projectDoc[role.field] = this.project[role.field];
				}
				for (const ac of this.assignmentConfigs) {
					projectDoc[ac.projectField] = this.project[ac.projectField] ?? [];
				}
				projectDoc.permissions = this.project.permissions;
				await this.projectService.save(projectDoc);
				// Projekt neu laden um currentProjectDoc zu aktualisieren
				await this.projectService.loadProject(this.editProjectId!);
				this.activeModal.close(projectDoc);
			}
		} else {
			// Neu: Projekt erstellen — sicherstellen, dass die DB bereit ist
			await this.projectService.ensureRemoteDb();
			const savedProject = await this.projectService.create(this.project);
			if (savedProject) {
				await this.projectService.loadProject(savedProject._id);
				// Dialog sofort schließen, Post-Create-Handler laufen im Hintergrund
				this.activeModal.close(savedProject);
				for (const handler of this.postCreateHandlers) {
					try {
						await handler.onProjectCreated(savedProject, this.sectionData);
					} catch (err) {
						console.error('[ProjectNew] Post-Create-Handler Fehler:', err);
					}
				}
			}
		}
	}
}
